import { useState, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import type { CreditAppSearchFormData, CreditAppSearchResult, SearchError, UseSearchCreditAppsReturn } from '../types';
import { stripPhoneFormatting } from '../utils/validators';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import { apiConfigHelper, locHrefUtil, MANAGER_VIEW_APP_PREFIX } from '@makemydeal/dr-shared-ui-utils';
import type { Consumer, IDealSearchDeal } from '@makemydeal/dr-shared-bff-types';
import { logNewRelicPageAction } from '@makemydeal/dr-activities-common';
import { dateUtils } from '@makemydeal/dr-common-utils';

// Map deal type for display
const dealTypeMap: Record<string, string> = {
    lease: 'Lease Deal',
    undecided: 'Undecided',
    balloon: 'Balloon Deal',
    finance: 'Finance Deal'
};

/**
 * Custom hook to manage credit app search API integration
 * Handles all HTTP response codes and stores results in component state
 */
export const useSearchCreditApps = (): UseSearchCreditAppsReturn => {
    const [searchResults, setSearchResults] = useState<CreditAppSearchResult[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<SearchError | null>(null);
    const abortControllerRef = useRef<AbortController | null>(null);
    const currentRequestIdRef = useRef<number>(0);

    // Get required parameters from Redux
    const commonOrgId = useSelector(offerReduxSelectors.getCommonOrgId);
    const dealId = useSelector(offerReduxSelectors.getDealXgId);

    /**
     * Perform search by calling BFF dealSearch endpoint
     * Endpoint: /api/commonOrg/{orgId}/deals/search
     * Handles all HTTP response codes: 200, 204, 400, 5xx
     */
    const performSearch = useCallback(
        async (criteria: CreditAppSearchFormData) => {
            // Increment request ID to track current request
            currentRequestIdRef.current += 1;
            const thisRequestId = currentRequestIdRef.current;

            // Cancel any ongoing search
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }

            abortControllerRef.current = new AbortController();
            setIsLoading(true);
            setError(null);
            setSearchResults([]);

            try {
                // Build URL-encoded query string with BFF parameter names
                const queryParams = new URLSearchParams();
                if (criteria.lastName) queryParams.append('lastName', criteria.lastName);
                if (criteria.phone) queryParams.append('phoneNumber', stripPhoneFormatting(criteria.phone));
                if (criteria.email) queryParams.append('emailAddress', criteria.email);
                if (dealId) queryParams.append('dealId', dealId); // Required parameter - current working deal

                const { url } = apiConfigHelper.buildBffApiUrl(
                    MANAGER_VIEW_APP_PREFIX,
                    locHrefUtil.getLocHref(),
                    `/commonOrg/${commonOrgId}/deals/search?${queryParams.toString()}`
                );
                const response = await fetch(url, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    signal: abortControllerRef.current!.signal
                });

                // Handle 204 No Content - successful search but no results
                if (response.status === 204) {
                    logNewRelicPageAction('MV:CreditAppSearch - Search completed', {
                        success: true,
                        httpStatus: 204,
                        resultCount: 0
                    });
                    setError({ hasError: false, hasNoResults: true });
                    return;
                }

                // Handle 400 Bad Request - invalid search parameters
                if (response.status === 400) {
                    logNewRelicPageAction('MV:CreditAppSearch - Search completed', {
                        success: false,
                        httpStatus: 400,
                        errorType: 'BAD_REQUEST'
                    });
                    setError({ hasError: true, hasNoResults: false });
                    return;
                }

                // Handle 5xx Server Error
                if (response.status >= 500) {
                    logNewRelicPageAction('MV:CreditAppSearch - Search completed', {
                        success: false,
                        httpStatus: response.status,
                        errorType: 'SERVER_ERROR'
                    });
                    setError({ hasError: true, hasNoResults: false });
                    return;
                }

                // Handle 200 OK - successful search with results
                if (response.ok) {
                    const data = await response.json();
                    // BFF returns { matchedDeals: [...], filteredDeals: [...] }

                    // The BFF returns 200 with filteredDeals but no matchedDeals when every deal it
                    // found was excluded by its business rules. There is nothing the user can attach
                    // in that case, so treat it the same as 204.
                    if (!data.matchedDeals?.length) {
                        logNewRelicPageAction('MV:CreditAppSearch - Search completed', {
                            success: true,
                            httpStatus: 200,
                            resultCount: 0
                        });
                        setError({ hasError: false, hasNoResults: true });
                        return;
                    }

                    // Store raw deal data with minimal transformation for display
                    const transformedResults: CreditAppSearchResult[] = data.matchedDeals.map((deal: IDealSearchDeal) => {
                        // Extract customer info from participants array or dealStructure.participants.buyer
                        const buyer = deal.dealStructure.participants?.buyer || ({} as Consumer);

                        // Extract vehicle info from dealStructure.vehicles.sellingVehicle
                        const vehicle = deal.dealStructure.vehicles?.sellingVehicle || {};

                        const dealType = dealTypeMap[deal.dealStructure.dealType.toLowerCase()] || 'Finance Deal';

                        const firstName = buyer.firstName || '';
                        const lastName = buyer.lastName || '';
                        const customerName = `${firstName} ${lastName}`.trim() || '';
                        const phone = buyer.phoneNumber || '';
                        const email = buyer.emailAddress || '';

                        return {
                            creditAppId: deal.dealStructure.creditApplication!.creditApplicationId, // deals without creditApplicationId are filtered out by the BFF
                            dealType,
                            createdDate: deal.dealStructure.versionCreatedOn
                                ? dateUtils.formatAsMMDDYYYY(new Date(deal.dealStructure.versionCreatedOn))
                                : '',
                            customerName,
                            phone,
                            email,
                            vehicleYear: vehicle.year?.toString() || '',
                            vehicleMake: vehicle.make || '',
                            vehicleModel: vehicle.model || '',
                            vehicleTrim: vehicle.trim || '',
                            vin: vehicle.vin || '',
                            stockNumber: vehicle.stockNumber || '',
                            rawDeal: deal // Store the complete raw deal data for merge operation
                        };
                    });

                    setSearchResults(transformedResults);
                    logNewRelicPageAction('MV:CreditAppSearch - Search completed', {
                        success: true,
                        httpStatus: 200,
                        resultCount: transformedResults.length
                    });
                    return;
                }

                // Handle unexpected status codes
                logNewRelicPageAction('MV:CreditAppSearch - Search completed', {
                    success: false,
                    httpStatus: response.status,
                    errorType: 'UNEXPECTED_STATUS'
                });
                setError({ hasError: true, hasNoResults: false });
            } catch (err) {
                // Handle network errors (no response from server)
                // Don't set error state if the request was aborted
                if (err instanceof Error && err.name === 'AbortError') {
                    return;
                }
                logNewRelicPageAction('MV:CreditAppSearch - Search completed', {
                    success: false,
                    httpStatus: null,
                    errorType: 'NETWORK_ERROR',
                    errorMessage: err instanceof Error ? err.message : 'Unknown error'
                });
                setError({ hasError: true, hasNoResults: false });
            } finally {
                // Only set isLoading to false if this is still the current request
                if (thisRequestId === currentRequestIdRef.current) {
                    setIsLoading(false);
                }
            }
        },
        [commonOrgId, dealId]
    );

    /**
     * Clear search results and error state
     */
    const clearResults = useCallback(() => {
        setSearchResults([]);
        setError(null);
    }, []);

    /**
     * Cancel any ongoing search
     */
    const cancelSearch = useCallback(() => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }
    }, []);

    return {
        searchResults,
        isLoading,
        error,
        performSearch,
        clearResults,
        cancelSearch
    };
};
